import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dropdown, Avatar, Space, message } from 'antd';
import { Person } from '@mui/icons-material';
import { MainHeaderWrapper } from './MainHeader.style';
import { SearchBar } from '../SearchBar';
import { NotificationDropdown } from '../NotificationDropdown';
import { usePlaylistContext } from '../../context/PlaylistContext';
import { useLoginContext } from '../../context';
import { usePackageContext } from '../../context/PackageContext';
import { SideBarAccount } from '../SideBar/SideBarAccount.style';
import { SideBarTabStyle } from '../SideBar/SideBar.style';
import { LogoutIcon } from '../../assets/index';
import {
  addItemToStorage,
  deleteItemFromStorage,
  clearSiteStorage,
  Auth0Logout,
  getRefreshedToken,
} from '../../utils';
import {
  SPOTIFY_CODE,
  SPOTIFY_REFRESH_TOKEN,
  SPOTIFY_TOKEN,
  SPOTIFY_USER_INFO,
} from '../../constants/storage';
import axios from 'axios';
import { BASE_URL } from '../../constants/api-urls';
import { SpotifyReconnectModal } from '../SpotifyReconnectModal';

const resolveAvatarSrc = (image: unknown): string | undefined => {
  if (!image || image === 'dummy') {
    return undefined;
  }

  if (typeof image === 'string') {
    return image;
  }

  if (Array.isArray(image) && image[0]?.url) {
    return image[0].url;
  }

  return undefined;
};

const MainHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: playlistState } = usePlaylistContext();
  const { dispatch: loginDispatch } = useLoginContext();
  const { clearCache } = usePackageContext();
  const [activeTab, setActiveTab] = useState('');
  const [theme, setTheme] = useState('dark');
  const [spotifyConnectedUser, setSpotifyConnectedUser] = useState<any>({});
  const [accounts, setAccounts] = useState([]);
  const [user, setUser] = useState<any>({});
  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const [hasSpotifyConnection, setHasSpotifyConnection] = useState(true);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [reconnectLabel, setReconnectLabel] = useState('Connect Spotify');
  const [reconnectModalOpen, setReconnectModalOpen] = useState(false);
  const [pendingReconnectIdentity, setPendingReconnectIdentity] = useState<any>(null);

  // Shared profiles functionality
  const userId = JSON.parse(localStorage.getItem('userInfo'))?.userId;
  const [sharedProfiles, setSharedProfiles] = useState([]);

  const tabs = [
    { id: 'logo', label: 'Logo', url: '/home' },
    { id: 'account-overview', label: 'Account overview', url: '/settings' },
    { id: 'user-guidelines', label: 'User guidelines', url: '/user-guide' },
    { id: 'support', label: 'Support', url: '/support' },
  ];

  useEffect(() => {
    setTheme(playlistState?.isWholeTheme === false ? 'dark' : 'light');
  }, [playlistState]);

  useEffect(() => {
    const refreshConnectionState = () => {
      const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || 'null');
      const hasConnection = localStorage.getItem('hasSpotifyConnection') === 'true';
      import('../../utils/spotifyAuth').then(
        ({ needsSpotifyReconnect, resolveReconnectIdentity, getReconnectDisplayName }) => {
          const reconnect = needsSpotifyReconnect();
          setSpotifyConnectedUser(spotifyUser);
          setUser(JSON.parse(localStorage.getItem('userInfo')));
          setNeedsReconnect(reconnect);
          setHasSpotifyConnection(!!(spotifyUser || hasConnection) && !reconnect);
          const identity = resolveReconnectIdentity();
          const name =
            identity?.username || getReconnectDisplayName() || spotifyUser?.username || 'Spotify';
          setReconnectLabel(reconnect ? `Reconnect ${name}` : 'Connect Spotify');
        }
      );
    };
    refreshConnectionState();
    window.addEventListener('spotify:reauth-required', refreshConnectionState);
    return () => window.removeEventListener('spotify:reauth-required', refreshConnectionState);
  }, []);

  useEffect(() => {
    const userId = JSON.parse(localStorage.getItem('userInfo'))?.userId;
    const fetchAccounts = async () => {
      try {
        const config = {
          headers: {
            'Content-Type': 'application-json',
          },
        };
        const response = await axios.get(`${BASE_URL}/user/${userId}/data`, config);
        if (response) {
          setAccounts(response?.data?.data);
        }
      } catch (error) {}
    };
    if (userId) {
      fetchAccounts();
    }
  }, []);

  useEffect(() => {
    let paths = location.pathname.split('/');
    let currentPath = paths[paths.length - 1];

    // Map current path to tab IDs
    if (currentPath === 'settings') {
      setActiveTab('account-overview');
    } else if (currentPath === 'user-guide') {
      setActiveTab('user-guidelines');
    } else if (currentPath === 'support') {
      setActiveTab('support');
    } else {
      setActiveTab(currentPath);
    }
  }, [location]);

  // Load shared profiles and user data on mount
  useEffect(() => {
    const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser'));
    setSpotifyConnectedUser(spotifyUser);

    //Get User Profile
    const newData = async () => {
      try {
        const config = {
          headers: {
            'Content-Type': 'application-json',
          },
        };
        const response = await axios.get(`${BASE_URL}/user/${userId}/data`, config);
        if (response) {
          setAccounts(response?.data?.data);
        }
      } catch (error) {}
    };

    if (userId) {
      newData();
      fetchSharedProfiles();
    }

    // Add event listener for invitation acceptance
    const handleInvitationAccepted = () => {
      fetchSharedProfiles();
    };

    window.addEventListener('invitationAccepted', handleInvitationAccepted);

    return () => {
      window.removeEventListener('invitationAccepted', handleInvitationAccepted);
    };
  }, [userId]);

  const handleLogout = async () => {
    try {
      const { clearAllSpotifyBrowserState } = await import('../../utils/spotifyAuth');
      clearAllSpotifyBrowserState();
    } catch {
      deleteItemFromStorage(SPOTIFY_TOKEN);
      deleteItemFromStorage(SPOTIFY_CODE);
      deleteItemFromStorage(SPOTIFY_USER_INFO);
      localStorage.removeItem('spotifyUser');
    }
    localStorage.removeItem('userInfo');
    clearSiteStorage();
    clearCache();
    loginDispatch({
      type: 'SET_LOGIN',
      payload: false,
    });
    Auth0Logout();
  };

  const handleTabClick = (tab: any) => {
    if (tab.id === 'logo') {
      navigate(tab.url);
    } else {
      setActiveTab(tab.id);
      navigate(tab.url);
    }
  };

  const proceedToSpotifyOAuth = (mode: 'reconnect' | 'first_connect', identity: any) => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('userInfo') || '{}');
      if (storedUser?.campaignsOnboardingRequired === true) {
        localStorage.setItem('postSpotifyRedirect', '/campaigns?onboarding=1');
      }
    } catch {
      // ignore
    }
    import('../../utils/spotifyAuth').then(({ buildSpotifyAuthorizeUrl }) => {
      window.location.replace(
        buildSpotifyAuthorizeUrl({
          showDialog: true,
          mode,
          identity: mode === 'reconnect' ? identity : null,
        })
      );
    });
  };

  const handleConnectSpotify = () => {
    import('../../utils/spotifyAuth').then(
      ({ resolveReconnectIdentity, needsSpotifyReconnect }) => {
        const reconnect = needsSpotifyReconnect();
        const reconnectProfile = reconnect ? resolveReconnectIdentity() : null;
        if (reconnectProfile?.isShared) {
          message.warning(
            'This Spotify account’s connection expired. Ask the account owner to reconnect Spotify in Songplace.'
          );
          return;
        }
        if (reconnect) {
          setPendingReconnectIdentity(reconnectProfile);
          setReconnectModalOpen(true);
          return;
        }
        proceedToSpotifyOAuth('first_connect', null);
      }
    );
  };

  const handleConfirmReconnect = () => {
    setReconnectModalOpen(false);
    proceedToSpotifyOAuth('reconnect', pendingReconnectIdentity);
  };

  // Fetch shared profiles
  const fetchSharedProfiles = async () => {
    if (!userId) {
      return;
    }

    try {
      const config = {
        headers: {
          'Content-Type': 'application-json',
        },
      };
      const response = await axios.get(`${BASE_URL}/shared-profiles?userId=${userId}`, config);
      if (response?.data?.success) {
        const fetchedSharedProfiles = response?.data?.data || [];
        setSharedProfiles(fetchedSharedProfiles);

        // Check if currently on a shared profile that was revoked
        const currentSpotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
        if (currentSpotifyUser.isShared === true && currentSpotifyUser.profileId) {
          // Check if current shared profile still exists in the fetched list
          const currentProfileStillShared = fetchedSharedProfiles.some(
            (p: any) => p.id === currentSpotifyUser.profileId
          );

          if (!currentProfileStillShared) {
            console.log(
              '⚠️  [MainHeader] Current shared profile no longer available, switching back to own profile...'
            );

            // Get user's own profiles
            const ownProfilesResponse = await axios.get(`${BASE_URL}/user/${userId}/data`, config);
            if (ownProfilesResponse?.data?.data && ownProfilesResponse.data.data.length > 0) {
              const firstOwnProfile = ownProfilesResponse.data.data[0];
              await returnToOwnProfile(firstOwnProfile._id);
            } else {
              // No connected Spotify profile, clear shared tokens and route to connect flow
              deleteItemFromStorage(SPOTIFY_TOKEN);
              deleteItemFromStorage(SPOTIFY_REFRESH_TOKEN);
              deleteItemFromStorage(SPOTIFY_CODE);
              deleteItemFromStorage(SPOTIFY_USER_INFO);
              localStorage.removeItem('spotifyUser');
              localStorage.setItem('hasSpotifyConnection', 'false');
              localStorage.removeItem('spotifyBannerDismissed');

              window.location.replace('/settings');
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ [MainHeader] Error fetching shared profiles:', error);
    }
  };

  // Switch to shared profile
  const switchToSharedProfile = async (profileId: string) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/shared-profiles/${profileId}/tokens?userId=${userId}`
      );

      if (response?.data?.success) {
        const { token, refreshToken, ownerUserId, username, ownerPlanType } = response.data.data;

        // Update LoginContext with owner's tokens
        loginDispatch({
          type: 'SWITCH_TO_SHARED_PROFILE',
          payload: {
            token,
            refreshToken,
            activeProfileType: 'shared',
            activeProfileId: profileId,
            sharedProfileOwner: {
              userId: ownerUserId,
              username,
              profileId,
            },
          },
        });

        // Update localStorage with owner's tokens
        addItemToStorage(SPOTIFY_TOKEN, token);
        addItemToStorage(SPOTIFY_REFRESH_TOKEN, refreshToken);

        // Update spotifyUser to reflect shared profile with owner's plan type
        const spotifyUser = {
          username,
          profileId,
          isShared: true,
          ownerId: ownerUserId,
          code: token,
          userId: userId,
          ownerPlanType: ownerPlanType, // Store owner's plan for feature restrictions
        };
        localStorage.setItem('spotifyUser', JSON.stringify(spotifyUser));

        // Reload to refresh UI
        window.location.replace('/home');
      }
    } catch (error) {
      console.error('Failed to switch to shared profile:', error);
    }
  };

  // Return to own profile
  const returnToOwnProfile = async (ownProfileId: string) => {
    try {
      const response = await axios.get(`${BASE_URL}/user/${userId}/data`);
      if (response?.data?.data) {
        const ownProfile = response.data.data.find((p: any) => p._id === ownProfileId);
        if (ownProfile) {
          // Pin active profile first so reconnect identity can never stick to the previous one.
          localStorage.setItem('SPOTIFY_ACTIVE_PROFILE_ID', ownProfileId);

          const identity = {
            profileId: String(ownProfileId),
            spotifyID: ownProfile.spotifyID || null,
            username: ownProfile.username,
            image: ownProfile.image,
          };

          const { clearSpotifySession, markSpotifyConnected } =
            await import('../../utils/spotifyAuth');

          // Drop any previous profile's reconnect snapshot before applying this profile's health.
          localStorage.removeItem('SPOTIFY_RECONNECT_PROFILE');
          localStorage.removeItem('SPOTIFY_NEEDS_RECONNECT');
          localStorage.removeItem('SPOTIFY_RECONNECT_NOTICE');

          // Server-marked dead refresh: do not rehydrate secrets
          if (ownProfile.spotifyNeedsReconnect === true) {
            clearSpotifySession(identity, 'profile_switch_needs_reconnect');
            loginDispatch({
              type: 'SPOTIFY_REAUTH_REQUIRED',
              payload: {
                profileId: String(ownProfileId),
                username: ownProfile.username,
                spotifyID: ownProfile.spotifyID,
              },
            });
            window.location.replace('/home');
            return;
          }

          if (!ownProfile.refresh) {
            clearSpotifySession(identity, 'profile_switch_missing_refresh');
            loginDispatch({
              type: 'SPOTIFY_REAUTH_REQUIRED',
              payload: {
                profileId: String(ownProfileId),
                username: ownProfile.username,
                spotifyID: ownProfile.spotifyID,
              },
            });
            window.location.replace('/home');
            return;
          }

          let token = ownProfile.token;
          let refreshToken = ownProfile.refresh;
          try {
            const refreshResponse = await getRefreshedToken(ownProfile.refresh, identity);
            token = refreshResponse?.data?.access_token;
            refreshToken = refreshResponse?.data?.refresh_token || ownProfile.refresh;
            if (!token) {
              throw new Error('Spotify did not return an access token');
            }
          } catch (error: any) {
            if (error?.code === 'SPOTIFY_REAUTH_REQUIRED') {
              // getRefreshedToken already cleared session for THIS identity
              window.location.replace('/home');
              return;
            }
            message.error('Could not validate this Spotify profile. Please try switching again.');
            return;
          }

          // Healthy profile: clear reconnect UI completely and load only this profile's tokens.
          markSpotifyConnected(identity);

          loginDispatch({
            type: 'SWITCH_TO_OWN_PROFILE',
            payload: {
              token,
              refreshToken,
              activeProfileType: 'own',
              activeProfileId: String(ownProfileId),
            },
          });

          addItemToStorage(SPOTIFY_TOKEN, token);
          addItemToStorage(SPOTIFY_REFRESH_TOKEN, refreshToken);

          const spotifyUser = {
            username: ownProfile.username,
            image: ownProfile.image,
            code: ownProfile.token,
            userId: userId,
            profileId: String(ownProfileId),
            spotifyID: ownProfile.spotifyID,
            spotify_user_id: ownProfile.spotifyID,
            needsReconnect: false,
          };
          localStorage.setItem('spotifyUser', JSON.stringify(spotifyUser));
          localStorage.setItem('hasSpotifyConnection', 'true');
          localStorage.setItem('SPOTIFY_ACTIVE_PROFILE_ID', String(ownProfileId));

          // Reload to refresh UI
          window.location.replace('/home');
        }
      }
    } catch (error) {
      console.error('Failed to return to own profile:', error);
    }
  };

  const SideBarTab = React.memo((props: any) => {
    const { title, icon, onClick, style } = props;
    return (
      <SideBarTabStyle onClick={onClick} style={{ cursor: 'pointer' }}>
        {icon}
        <p className="title" style={style}>
          {title}
        </p>
      </SideBarTabStyle>
    );
  });

  const activeAccount = accounts.find(
    (account: any) => String(account?._id) === String(spotifyConnectedUser?.profileId)
  );
  const headerAvatarSrc =
    resolveAvatarSrc(spotifyConnectedUser?.image) ?? resolveAvatarSrc(activeAccount?.image);
  const headerAvatarLabel = spotifyConnectedUser?.username || activeAccount?.username || '';

  const profileMenu = (
    <SideBarAccount>
      {accounts?.length ? <p className="dropDownHeading">MY ACCOUNTS</p> : <></>}
      <div className="menuLinks">
        {accounts.map((a: any, i: number) => {
          const currentSpotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || 'null');
          // Match by stable profile id — usernames can collide across Spotify accounts.
          const isActive =
            String(a?._id) === String(currentSpotifyUser?.profileId) &&
            !currentSpotifyUser?.isShared;

          return (
            <div
              key={`account-${i}`}
              className={`menuDiv ${isActive ? 'active' : ''}`}
              onClick={() => {
                if (userId) {
                  returnToOwnProfile(a?._id);
                }
              }}
            >
              <div className="imageName">
                <Avatar
                  className="avater1 account-span"
                  src={resolveAvatarSrc(a?.image)}
                  icon={
                    !resolveAvatarSrc(a?.image) ? (
                      <span className="spanner3">
                        {a?.username?.length >= 2 ? a.username[0] + a.username[1] : 'U'}
                      </span>
                    ) : undefined
                  }
                />
                {a?.username?.slice(0, 18)}
                {a?.username?.length > 18 ? '....' : ''}
              </div>
              <svg
                className="ticIcon"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.75 8.75L6.25 12.25L13.25 4.75"
                  stroke="#56BA32"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          );
        })}
      </div>

      {/* Shared Profiles Section */}
      {sharedProfiles && sharedProfiles.length > 0 && (
        <>
          {accounts?.length ? <hr /> : <></>}
          <p className="dropDownHeading">SHARED PROFILES</p>
          <div className="menuLinks">
            {sharedProfiles.map((profile: any, i: number) => {
              const currentSpotifyUser = JSON.parse(localStorage.getItem('spotifyUser'));
              const isActive =
                currentSpotifyUser?.isShared && currentSpotifyUser?.profileId === profile.id;
              return (
                <div
                  key={`shared-${i}`}
                  className={`menuDiv ${isActive ? 'active' : ''}`}
                  onClick={() => switchToSharedProfile(profile.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="imageName">
                    <Avatar
                      className="avater1 account-span"
                      src={resolveAvatarSrc(profile?.image)}
                      icon={
                        !resolveAvatarSrc(profile?.image) ? (
                          <span className="spanner3">
                            {profile?.username?.length >= 2
                              ? profile.username[0] + profile.username[1]
                              : 'U'}
                          </span>
                        ) : undefined
                      }
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span>
                        {profile?.username.slice(0, 18)}{' '}
                        {profile?.username.length > 18 ? '....' : ''}
                      </span>
                      <span style={{ fontSize: '10px', color: '#ffffffb3' }}>
                        by {profile?.ownerName || 'Unknown'}
                      </span>
                    </div>
                  </div>
                  <svg
                    className="ticIcon"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.75 8.75L6.25 12.25L13.25 4.75"
                      stroke="#56BA32"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              );
            })}
          </div>
        </>
      )}

      <hr />

      <SideBarTab
        style={{ fontSize: '12px' }}
        icon={
          theme === 'dark' ? (
            <Person sx={{ color: 'white', fontSize: '16px' }} />
          ) : (
            <Person sx={{ color: '#1D1D1D', fontSize: '16px' }} />
          )
        }
        title="Account overview"
        onClick={() => navigate('/settings')}
      />
      <SideBarTab
        style={{ fontSize: '12px' }}
        icon={theme === 'dark' ? <LogoutIcon color={'white'} /> : <LogoutIcon color={'#1D1D1D'} />}
        title="Logout"
        onClick={handleLogout}
      />
    </SideBarAccount>
  );

  return (
    <MainHeaderWrapper>
      <div className="header-container">
        <div className="header-tabs">
          {tabs.map(tab =>
            tab.id === 'logo' ? (
              <div key={tab.id} className="logo-container" onClick={() => handleTabClick(tab)}>
                {theme === 'dark' ? (
                  <img src="./static/songplace.png" alt="Songplace" className="header-logo" />
                ) : (
                  <img src="./static/logo.png" alt="Songplace" className="header-logo" />
                )}
              </div>
            ) : (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabClick(tab)}
              >
                {tab.label}
              </button>
            )
          )}
        </div>
        <div className="header-actions">
          <div className="search-bar-wrapper">
            <SearchBar />
          </div>
          {(!hasSpotifyConnection || needsReconnect) && (
            <button className="connect-spotify-btn" onClick={handleConnectSpotify}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
              </svg>
              {reconnectLabel}
            </button>
          )}
          <NotificationDropdown
            visible={notificationsVisible}
            onVisibleChange={setNotificationsVisible}
          />
          <Dropdown overlay={profileMenu} trigger={['click']} overlayClassName="profileDropdown">
            <div className="profile-avatar" onClick={e => e.preventDefault()}>
              <Space>
                <Avatar
                  className="avatar-icon"
                  size={32}
                  src={headerAvatarSrc}
                  icon={
                    !headerAvatarSrc ? (
                      <span className="avatar-text">
                        {headerAvatarLabel.length >= 2 ? (
                          headerAvatarLabel[0] + headerAvatarLabel[1]
                        ) : (
                          <Person
                            sx={{ color: theme === 'dark' ? 'white' : '#1D1D1D', fontSize: '16px' }}
                          />
                        )}
                      </span>
                    ) : undefined
                  }
                />
              </Space>
            </div>
          </Dropdown>
        </div>
      </div>
      <SpotifyReconnectModal
        visible={reconnectModalOpen}
        identity={pendingReconnectIdentity}
        onCancel={() => setReconnectModalOpen(false)}
        onConfirm={handleConfirmReconnect}
      />
    </MainHeaderWrapper>
  );
};

export default MainHeader;
